import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { X, Plus, AlertCircle } from "lucide-react";
import { 
  ConditionRule, 
  ConditionRules, 
  ConditionType, 
  ConditionOperator 
} from "@/types/workflow";
import { useProductAttributes } from "@/hooks/useProductAttributes";
import { FieldType } from "@/types/productAttributes";

interface ConditionBuilderProps {
  conditionRules?: ConditionRules;
  onChange: (rules: ConditionRules | undefined) => void;
  onClose?: () => void;
}

const operatorsByFieldType: Record<FieldType, ConditionOperator[]> = {
  [FieldType.SELECT]: [
    ConditionOperator.EQUALS,
    ConditionOperator.NOT_EQUALS,
    ConditionOperator.IN,
    ConditionOperator.NOT_IN
  ],
  [FieldType.MULTISELECT]: [
    ConditionOperator.CONTAINS,
    ConditionOperator.NOT_CONTAINS,
    ConditionOperator.IN,
    ConditionOperator.NOT_IN
  ],
  [FieldType.TEXT]: [
    ConditionOperator.EQUALS,
    ConditionOperator.NOT_EQUALS,
    ConditionOperator.CONTAINS,
    ConditionOperator.NOT_CONTAINS
  ],
  [FieldType.NUMBER]: [
    ConditionOperator.EQUALS,
    ConditionOperator.NOT_EQUALS,
    ConditionOperator.GREATER_THAN,
    ConditionOperator.LESS_THAN
  ],
  [FieldType.BOOLEAN]: [
    ConditionOperator.EQUALS,
    ConditionOperator.NOT_EQUALS
  ],
  [FieldType.DATE]: [
    ConditionOperator.EQUALS,
    ConditionOperator.NOT_EQUALS,
    ConditionOperator.GREATER_THAN,
    ConditionOperator.LESS_THAN
  ]
};

const operatorLabels: Record<ConditionOperator, string> = {
  [ConditionOperator.EQUALS]: "等於 (值完全相同)",
  [ConditionOperator.NOT_EQUALS]: "不等於 (值不相同)",
  [ConditionOperator.IN]: "在列表中 (符合任一選項)",
  [ConditionOperator.NOT_IN]: "不在列表中 (不符合任何選項)",
  [ConditionOperator.GREATER_THAN]: "大於 (數值或日期晚於)",
  [ConditionOperator.LESS_THAN]: "小於 (數值或日期早於)",
  [ConditionOperator.CONTAINS]: "包含 (文字中含有此內容)",
  [ConditionOperator.NOT_CONTAINS]: "不包含 (文字中沒有此內容)"
};

export const ConditionBuilder: React.FC<ConditionBuilderProps> = ({ 
  conditionRules, 
  onChange,
  onClose 
}) => {
  const { data: productAttributes = [], isLoading } = useProductAttributes();

  const isConditionComplete = (condition: ConditionRule): boolean => {
    if (!condition.field) return false;
    if (!condition.operator) return false;
    
    // All operators now require a value
    if (condition.value === null || condition.value === undefined || condition.value === "") {
      return false;
    }
    
    // For IN/NOT_IN operators, check if array is empty
    if ([ConditionOperator.IN, ConditionOperator.NOT_IN].includes(condition.operator)) {
      if (Array.isArray(condition.value) && condition.value.length === 0) {
        return false;
      }
    }
    
    return true;
  };

  const handleTypeChange = (type: ConditionType) => {
    onChange({
      type,
      conditions: conditionRules?.conditions || []
    });
  };

  const handleAddCondition = () => {
    // Check if there are any incomplete conditions
    const hasIncompleteCondition = conditionRules?.conditions.some(condition => {
      return !isConditionComplete(condition);
    });

    if (hasIncompleteCondition) {
      // Don't add new condition if there are incomplete ones
      return;
    }

    const newCondition: ConditionRule = {
      field: "",
      operator: ConditionOperator.EQUALS,
      value: null
    };

    onChange({
      type: conditionRules?.type || ConditionType.ALL,
      conditions: [...(conditionRules?.conditions || []), newCondition]
    });
  };

  const handleRemoveCondition = (index: number) => {
    if (!conditionRules) return;
    
    const newConditions = conditionRules.conditions.filter((_, i) => i !== index);
    
    if (newConditions.length === 0) {
      onChange(undefined);
    } else {
      onChange({
        ...conditionRules,
        conditions: newConditions
      });
    }
  };

  const handleConditionChange = (index: number, updates: Partial<ConditionRule>) => {
    if (!conditionRules) return;

    const newConditions = [...conditionRules.conditions];
    newConditions[index] = { ...newConditions[index], ...updates };

    onChange({
      ...conditionRules,
      conditions: newConditions
    });
  };

  const getFieldType = (fieldName: string): FieldType | undefined => {
    const attr = productAttributes.find(a => a.fieldName === fieldName);
    return attr?.fieldType;
  };

  const getFieldOptions = (fieldName: string): string[] => {
    const attr = productAttributes.find(a => a.fieldName === fieldName);
    return attr?.options || [];
  };

  const renderValueInput = (condition: ConditionRule, index: number) => {
    const fieldType = getFieldType(condition.field);
    const options = getFieldOptions(condition.field);

    // Multi-select for IN/NOT_IN operators
    if ([ConditionOperator.IN, ConditionOperator.NOT_IN].includes(condition.operator)) {
      return (
        <div className="space-y-2">
          <Label className="text-xs">值（多選）</Label>
          <div className="flex flex-wrap gap-2">
            {options.map(option => (
              <label key={option} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={(condition.value as string[] || []).includes(option)}
                  onChange={(e) => {
                    const currentValues = (condition.value as string[]) || [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter(v => v !== option);
                    handleConditionChange(index, { value: newValues });
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    // Field type specific inputs
    switch (fieldType) {
      case FieldType.SELECT:
        return (
          <Select
            value={condition.value as string || ""}
            onValueChange={(value) => handleConditionChange(index, { value })}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="選擇值" />
            </SelectTrigger>
            <SelectContent>
              {options.map(option => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case FieldType.BOOLEAN:
        return (
          <Select
            value={String(condition.value) || ""}
            onValueChange={(value) => handleConditionChange(index, { value: value === "true" })}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="選擇值" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">是</SelectItem>
              <SelectItem value="false">否</SelectItem>
            </SelectContent>
          </Select>
        );
      
      case FieldType.NUMBER:
        return (
          <Input
            type="number"
            value={condition.value as number || ""}
            onChange={(e) => handleConditionChange(index, { 
              value: e.target.value ? parseFloat(e.target.value) : null 
            })}
            placeholder="輸入數值"
          />
        );
      
      case FieldType.DATE:
        return (
          <Input
            type="date"
            value={condition.value as string || ""}
            onChange={(e) => handleConditionChange(index, { value: e.target.value })}
          />
        );
      
      default:
        return (
          <Input
            type="text"
            value={condition.value as string || ""}
            onChange={(e) => handleConditionChange(index, { value: e.target.value })}
            placeholder="輸入值"
          />
        );
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">載入產品屬性中...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">條件設定</CardTitle>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {productAttributes.length === 0 ? (
          <div className="text-center py-4">
            <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              尚未設定產品屬性。請先在產品屬性管理中設定。
            </p>
          </div>
        ) : (
          <>
            {/* Condition Type Selector */}
            {conditionRules && conditionRules.conditions.length > 1 && (
              <div className="flex items-center gap-4">
                <Label className="text-sm">條件邏輯：</Label>
                <div className="flex gap-2">
                  <Button
                    variant={conditionRules.type === ConditionType.ALL ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleTypeChange(ConditionType.ALL)}
                  >
                    全部符合 (ALL)
                  </Button>
                  <Button
                    variant={conditionRules.type === ConditionType.ANY ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleTypeChange(ConditionType.ANY)}
                  >
                    任一符合 (ANY)
                  </Button>
                </div>
              </div>
            )}

            {/* Conditions List */}
            <div className="space-y-3">
              {conditionRules?.conditions.map((condition, index) => {
                const fieldType = getFieldType(condition.field);
                const availableOperators = fieldType 
                  ? operatorsByFieldType[fieldType] 
                  : Object.values(ConditionOperator);

                return (
                  <div key={index} className="space-y-2">
                    <div className={`flex gap-2 items-start p-3 border rounded-lg ${
                      !isConditionComplete(condition) ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                    }`}>
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                        {/* Field Selector */}
                        <Select
                          value={condition.field}
                          onValueChange={(field) => handleConditionChange(index, { field })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="選擇屬性" />
                          </SelectTrigger>
                          <SelectContent>
                            {productAttributes.map(attr => (
                              <SelectItem key={attr.fieldName} value={attr.fieldName}>
                                {attr.displayName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Operator Selector */}
                        <Select
                          value={condition.operator}
                          onValueChange={(operator) => handleConditionChange(index, { 
                            operator: operator as ConditionOperator 
                          })}
                          disabled={!condition.field}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="選擇條件" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableOperators.map(op => (
                              <SelectItem key={op} value={op}>
                                {operatorLabels[op]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Value Input */}
                        <div>
                          {renderValueInput(condition, index)}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveCondition(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Validation Message */}
                    {!isConditionComplete(condition) && (
                      <div className="px-3">
                        <p className="text-xs text-amber-600">
                          {!condition.field && "請選擇屬性"}
                          {condition.field && !condition.operator && "請選擇條件"}
                          {condition.field && condition.operator && 
                           (condition.value === null || condition.value === undefined || condition.value === "" ||
                            ([ConditionOperator.IN, ConditionOperator.NOT_IN].includes(condition.operator) && 
                             Array.isArray(condition.value) && condition.value.length === 0)) && 
                           "請輸入值"}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Add Condition Button with Validation */}
            {(() => {
              const hasIncompleteCondition = conditionRules?.conditions.some(condition => !isConditionComplete(condition));
              const hasConditions = conditionRules?.conditions.length > 0;
              
              return (
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddCondition}
                    disabled={hasIncompleteCondition}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    新增條件
                  </Button>
                  {hasIncompleteCondition && hasConditions && (
                    <p className="text-xs text-amber-600 text-center">
                      請先完成現有條件的設定再新增新條件
                    </p>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </CardContent>
    </Card>
  );
};